import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SurveyQuestion {
  type: string;
  name: string;
  title: string;
  choices?: Array<{
    value: string;
    text: string;
  }>;
  isRequired?: boolean;
  maxSelectedChoices?: number;
  minSelectedChoices?: number;
  showSelectAllItem?: boolean;
  enableForMatching?: boolean;
}

export interface SurveyPage {
  name: string;
  title?: string;
  description?: string;
  elements: SurveyQuestion[];
}

export interface SurveyForm {
  title: string;
  description?: string;
  logoPosition?: string;
  pages: SurveyPage[];
}

export interface DepartmentDesignation {
  title: string;
  value: string;
}

export interface MatchingQuestion {
  type: string;
  menteeKey: string;
  mentorKey: string;
  options?: Record<string, any>;
  through?: string;
  optionsType?: string;
}

export interface QuestionPairing {
  mentorKey: string;
  menteeKey: string;
  type: 'oneHotEncoding' | 'timezone' | 'custom';
  syncOptions: boolean;
}

export interface FormCustomizationState {
  mentorForm: SurveyForm | null;
  menteeForm: SurveyForm | null;
  departments: DepartmentDesignation[];
  designations: DepartmentDesignation[];
  matchingQuestions: MatchingQuestion[];
  questionPairings: Map<string, string>;
  loading: boolean;
  error: string | null;
  validationErrors: string[];
  isFormValid: boolean;
  isInitialized: boolean;
}

const initialState: FormCustomizationState = {
  mentorForm: null,
  menteeForm: null,
  departments: [],
  designations: [],
  matchingQuestions: [],
  questionPairings: new Map(),
  loading: false,
  error: null,
  validationErrors: [],
  isFormValid: true,
  isInitialized: false,
};

const formCustomizationSlice = createSlice({
  name: "formCustomization",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFormData: (state, action: PayloadAction<{
      mentorForm: SurveyForm;
      menteeForm: SurveyForm;
      departments: DepartmentDesignation[];
      designations: DepartmentDesignation[];
      matchingQuestions: MatchingQuestion[];
    }>) => {
      const { mentorForm, menteeForm, departments, designations, matchingQuestions } = action.payload;
      state.mentorForm = mentorForm;
      state.menteeForm = menteeForm;
      state.departments = departments;
      state.designations = designations;
      state.matchingQuestions = matchingQuestions;
      
      // Generate question pairings map from matching questions
      const pairingsMap = new Map<string, string>();
      matchingQuestions.forEach(mq => {
        pairingsMap.set(mq.mentorKey, mq.menteeKey);
        pairingsMap.set(mq.menteeKey, mq.mentorKey);
      });
      state.questionPairings = pairingsMap;
      
      state.loading = false;
      state.error = null;
      state.isInitialized = true;
    },
    updateMentorForm: (state, action: PayloadAction<SurveyForm>) => {
      state.mentorForm = action.payload;
    },
    updateMenteeForm: (state, action: PayloadAction<SurveyForm>) => {
      state.menteeForm = action.payload;
    },
    updateDepartments: (state, action: PayloadAction<DepartmentDesignation[]>) => {
      state.departments = action.payload;
    },
    updateDesignations: (state, action: PayloadAction<DepartmentDesignation[]>) => {
      state.designations = action.payload;
    },
    updateMatchingQuestions: (state, action: PayloadAction<MatchingQuestion[]>) => {
      state.matchingQuestions = action.payload;
      
      // Update question pairings map
      const pairingsMap = new Map<string, string>();
      action.payload.forEach(mq => {
        pairingsMap.set(mq.mentorKey, mq.menteeKey);
        pairingsMap.set(mq.menteeKey, mq.mentorKey);
      });
      state.questionPairings = pairingsMap;
    },
    addDepartment: (state, action: PayloadAction<DepartmentDesignation>) => {
      state.departments.push(action.payload);
    },
    removeDepartment: (state, action: PayloadAction<string>) => {
      state.departments = state.departments.filter(dept => dept.value !== action.payload);
    },
    updateDepartment: (state, action: PayloadAction<{ index: number; department: DepartmentDesignation }>) => {
      const { index, department } = action.payload;
      if (state.departments[index]) {
        state.departments[index] = department;
      }
    },
    addDesignation: (state, action: PayloadAction<DepartmentDesignation>) => {
      state.designations.push(action.payload);
    },
    removeDesignation: (state, action: PayloadAction<string>) => {
      state.designations = state.designations.filter(designation => designation.value !== action.payload);
    },
    updateDesignation: (state, action: PayloadAction<{ index: number; designation: DepartmentDesignation }>) => {
      const { index, designation } = action.payload;
      if (state.designations[index]) {
        state.designations[index] = designation;
      }
    },
    setValidationResult: (state, action: PayloadAction<{ isValid: boolean; errors: string[] }>) => {
      const { isValid, errors } = action.payload;
      state.isFormValid = isValid;
      state.validationErrors = errors;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
      state.isFormValid = true;
    },
    synchronizeFormOptions: (state, action: PayloadAction<{ 
      questionName: string; 
      newChoices: Array<{ value: string; text: string }> 
    }>) => {
      const { questionName, newChoices } = action.payload;
      
      // Find the paired question
      const pairedQuestionName = state.questionPairings.get(questionName);
      
      // Update both mentor and mentee forms with synchronized options
      if (state.mentorForm && state.menteeForm) {
        // Update the original question
        state.mentorForm.pages.forEach(page => {
          const question = page.elements.find(q => q.name === questionName);
          if (question && question.choices) {
            question.choices = newChoices;
          }
        });
        
        state.menteeForm.pages.forEach(page => {
          const question = page.elements.find(q => q.name === questionName);
          if (question && question.choices) {
            question.choices = newChoices;
          }
        });
        
        // Update the paired question if it exists
        if (pairedQuestionName) {
          state.mentorForm.pages.forEach(page => {
            const question = page.elements.find(q => q.name === pairedQuestionName);
            if (question && question.choices) {
              question.choices = newChoices;
            }
          });
          
          state.menteeForm.pages.forEach(page => {
            const question = page.elements.find(q => q.name === pairedQuestionName);
            if (question && question.choices) {
              question.choices = newChoices;
            }
          });
        }
      }
    },
    addSkillCategory: (state, action: PayloadAction<{
      skillName: string;
      skillTitle: string;
      skillChoices: Array<{ value: string; text: string }>;
    }>) => {
      const { skillName, skillTitle, skillChoices } = action.payload;
      
      // Create new skill question
      const skillQuestion: SurveyQuestion = {
        name: skillName,
        type: 'tagbox',
        title: skillTitle,
        choices: skillChoices,
        enableForMatching: true,
        isRequired: false
      };
      
      // Add to skills page (page2) in both forms
      if (state.mentorForm && state.menteeForm) {
        const mentorSkillsPage = state.mentorForm.pages.find(p => p.name === 'page2');
        const menteeSkillsPage = state.menteeForm.pages.find(p => p.name === 'page2');
        
        if (mentorSkillsPage) {
          mentorSkillsPage.elements.push(skillQuestion);
        }
        
        if (menteeSkillsPage) {
          menteeSkillsPage.elements.push(skillQuestion);
        }
      }
      
      // Add matching question entry
      const newMatchingEntry: MatchingQuestion = {
        type: 'oneHotEncoding',
        mentorKey: skillName,
        menteeKey: skillName
      };
      
      state.matchingQuestions.push(newMatchingEntry);
      
      // Update pairings map
      state.questionPairings.set(skillName, skillName);
    },
    removeSkillCategory: (state, action: PayloadAction<string>) => {
      const skillName = action.payload;
      
      // Remove from both forms
      if (state.mentorForm && state.menteeForm) {
        state.mentorForm.pages.forEach(page => {
          page.elements = page.elements.filter(q => q.name !== skillName);
        });
        
        state.menteeForm.pages.forEach(page => {
          page.elements = page.elements.filter(q => q.name !== skillName);
        });
      }
      
      // Remove from matching questions
      state.matchingQuestions = state.matchingQuestions.filter(
        mq => mq.mentorKey !== skillName && mq.menteeKey !== skillName
      );
      
      // Remove from pairings map
      state.questionPairings.delete(skillName);
    },
    removeQuestionFromForm: (state, action: PayloadAction<{
      formType: 'mentor' | 'mentee';
      questionName: string;
    }>) => {
      const { formType, questionName } = action.payload;
      const form = formType === 'mentor' ? state.mentorForm : state.menteeForm;
      
      if (form) {
        // Remove question from all pages
        form.pages.forEach(page => {
          page.elements = page.elements.filter(q => q.name !== questionName);
        });
        
        // Remove from matching questions if it's paired
        state.matchingQuestions = state.matchingQuestions.filter(
          mq => mq.mentorKey !== questionName && mq.menteeKey !== questionName
        );
        
        // Clean up pairings
        const pairedWith = state.questionPairings.get(questionName);
        if (pairedWith) {
          state.questionPairings.delete(questionName);
          state.questionPairings.delete(pairedWith);
        }
      }
    },
    addQuestionToForm: (state, action: PayloadAction<{
      formType: 'mentor' | 'mentee';
      pageName: string;
      question: SurveyQuestion;
    }>) => {
      const { formType, pageName, question } = action.payload;
      const form = formType === 'mentor' ? state.mentorForm : state.menteeForm;
      
      if (form) {
        const page = form.pages.find(p => p.name === pageName);
        if (page) {
          page.elements.push(question);
        }
      }
    },
    setQuestionPairings: (state, action: PayloadAction<Map<string, string>>) => {
      state.questionPairings = action.payload;
    },
    resetFormCustomization: (state) => {
      return initialState;
    },
  },
});

export const {
  setLoading,
  setError,
  setFormData,
  updateMentorForm,
  updateMenteeForm,
  updateDepartments,
  updateDesignations,
  updateMatchingQuestions,
  addDepartment,
  removeDepartment,
  updateDepartment,
  addDesignation,
  removeDesignation,
  updateDesignation,
  setValidationResult,
  clearValidationErrors,
  synchronizeFormOptions,
  addSkillCategory,
  removeSkillCategory,
  removeQuestionFromForm,
  addQuestionToForm,
  setQuestionPairings,
  resetFormCustomization,
} = formCustomizationSlice.actions;

export default formCustomizationSlice.reducer;